import React, { useState } from 'react';
import classNames from 'classnames';

import css from './MenuLabel.module.css';

/**
 * The label button of the menu. MenuLabel is the only always visible part of Menu.
 * Clicking it toggles visibility of MenuContent.
 * Note: the state is kept outside this component. <Menu> keeps track of it.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.isOpenClassName overwrite components own css.isOpen
 * @param {ReactNode} props.children
 * @param {boolean} props.isOpen
 * @param {Function} props.onToggleActive
 * @param {string?} props.ariaControls id of the menu controlled by the label
 * @returns {JSX.Element} label button
 */
const MenuLabel = props => {
  const [clicked, setClicked] = useState(false);
  const {
    children,
    id,
    className,
    rootClassName,
    isOpen,
    isOpenClassName,
    onToggleActive,
    ariaLabel,
    ariaControls,
  } = props;

  const onClick = e => {
    e.stopPropagation();
    e.preventDefault();
    onToggleActive();

    // Don't show focus outline if user just clicked the element with mouse
    // tab + enter creates also a click event, but its location is origin.
    const nativeEvent = e.nativeEvent;
    const isRealClick = !(nativeEvent.clientX === 0 && nativeEvent.clientY === 0);
    if (isRealClick) {
      setClicked(true);
    }
  };

  const onBlur = () => {
    setClicked(false);
  };

  const rootClass = rootClassName || css.root;
  const isOpenClass = isOpenClassName || css.isOpen;
  const classes = classNames(rootClass, className, {
    [css.clicked]: clicked,
    [isOpenClass]: isOpen,
  });

  const ariaLabelMaybe = ariaLabel ? { ['aria-label']: ariaLabel } : {};
  const ariaControlsMaybe = ariaControls ? { ['aria-controls']: ariaControls } : {};

  return (
    <button
      id={id}
      type="button"
      className={classes}
      onClick={onClick}
      onBlur={onBlur}
      aria-haspopup="menu"
      aria-expanded={isOpen}
      {...ariaLabelMaybe}
      {...ariaControlsMaybe}
    >
      {children}
    </button>
  );
};

export default MenuLabel;
